import { Observable, Subject, BehaviorSubject, Subscription } from 'rxjs'
import {
  IAudioCaptureStrategy,
  AudioLevelInfo,
  AudioChunk,
  CaptureStatus,
  AudioCaptureError,
} from '../audio-capture-strategy.interface'
import {
  AudioRecordingService,
  AudioChunk as RecordingAudioChunk,
  AudioLevelInfo as RecordingAudioLevelInfo,
} from '../../audio-recording.service'
import { AudioLevelMonitorService } from '../../audio-level-monitor.service'

/**
 * MediaRecorder audio capture strategy
 *
 * Wrapper around AudioRecordingService for MediaRecorder mode.
 * Uses VAD-based chunking for recording audio segments.
 *
 * This strategy coordinates:
 * - AudioRecordingService: Provides VAD-based audio recording with automatic chunking
 * - AudioLevelMonitorService: Not used (AudioRecordingService has built-in level monitoring)
 *
 * The existing AudioRecordingService already includes:
 * - VAD-based silence detection with configurable thresholds
 * - Automatic audio chunking when speech ends
 * - Audio level monitoring with frequency analysis
 * - MediaRecorder-based audio capture with WebM format
 *
 * Requirements:
 * - 6.5: Coordinate AudioRecordingService with VAD-based chunking
 * - 11.5: Maintain same VAD settings (existing settings preserved)
 * - 2.5: Normalize output format (base64-encoded chunks)
 * - 2.1: Provide unified interface for audio capture
 * - 2.6: Implement audio level monitoring and status management
 *
 * @example
 * ```typescript
 * const strategy = new MediaRecorderStrategy(audioRecordingService, audioLevelMonitorService)
 * await strategy.initialize('device-id-123')
 * await strategy.start()
 *
 * strategy.getAudioChunk$().subscribe(chunk => {
 *   console.log('Audio chunk:', chunk)
 * })
 *
 * await strategy.stop()
 * ```
 */
export class MediaRecorderStrategy implements IAudioCaptureStrategy {
  private audioLevelSubject = new Subject<AudioLevelInfo>()
  private audioChunkSubject = new Subject<AudioChunk>()
  private statusSubject = new BehaviorSubject<CaptureStatus>({
    isInitialized: false,
    isCapturing: false,
    deviceId: null,
  })
  private errorSubject = new Subject<AudioCaptureError>()

  private isInitialized = false
  private isCapturing = false
  private deviceId: string | null = null
  private chunkSequence = 0

  // Subscriptions for cleanup
  private audioChunkSubscription?: Subscription
  private audioLevelSubscription?: Subscription
  private processingSubscription?: Subscription

  constructor(
    private audioRecordingService: AudioRecordingService,
    private audioLevelMonitorService: AudioLevelMonitorService,
  ) {}

  /**
   * Initialize MediaRecorder strategy
   *
   * Prepares for audio capture. Must be called before start().
   * Note: Device ID is stored but not used until start() is called.
   *
   * @param deviceId - Optional audio input device ID
   */
  async initialize(deviceId?: string | null): Promise<void> {
    try {
      this.deviceId = deviceId ?? null
      this.isInitialized = true
      this.chunkSequence = 0

      // Emit initialized status
      this.statusSubject.next({
        isInitialized: true,
        isCapturing: false,
        deviceId: this.deviceId,
      })
    } catch (error) {
      this.isInitialized = false
      throw error
    }
  }

  /**
   * Start audio capture
   *
   * Begins MediaRecorder-based audio capture with VAD.
   * Emits audio chunks via getAudioChunk$() and levels via getAudioLevel$().
   *
   * @throws AudioCaptureError if not initialized or capture fails
   */
  async start(): Promise<void> {
    if (!this.isInitialized) {
      this.errorSubject.next({
        code: 'NOT_INITIALIZED',
        message:
          'Cannot start capture: strategy not initialized. Call initialize() first.',
        canRetry: true,
        context: {},
      })
      return
    }

    if (this.isCapturing) {
      return
    }

    try {
      // Subscribe to AudioRecordingService streams before starting
      this.subscribeToAudioRecordingService()

      // Start continuous recording with VAD
      await this.audioRecordingService.startContinuousRecording()

      this.isCapturing = true

      // Emit capturing status
      this.statusSubject.next({
        isInitialized: true,
        isCapturing: true,
        deviceId: this.deviceId,
      })
    } catch (error) {
      this.isCapturing = false
      const captureError: AudioCaptureError = {
        code: 'START_FAILED',
        message: `Failed to start audio capture: ${error instanceof Error ? error.message : String(error)}`,
        canRetry: true,
        context: { deviceId: this.deviceId, error },
      }
      this.errorSubject.next(captureError)
      throw error
    }
  }

  /**
   * Stop audio capture
   *
   * Stops capturing audio and releases resources.
   * Does not fully clean up - use initialize() again to restart.
   */
  async stop(): Promise<void> {
    if (!this.isCapturing) {
      return
    }

    try {
      // Stop continuous recording
      this.audioRecordingService.stopContinuousRecording()

      // Cleanup subscriptions
      this.cleanupSubscriptions()

      this.isCapturing = false

      // Emit stopped status
      this.statusSubject.next({
        isInitialized: true,
        isCapturing: false,
        deviceId: this.deviceId,
      })
    } catch (error) {
      console.error('Error stopping MediaRecorder capture:', error)
    }
  }

  /**
   * Subscribe to AudioRecordingService observables
   *
   * Sets up subscriptions to bridge audio data and levels from
   * AudioRecordingService to this strategy's observables.
   */
  private subscribeToAudioRecordingService(): void {
    // Subscribe to audio chunks
    this.audioChunkSubscription =
      this.audioRecordingService.audioChunk$.subscribe(
        (chunk: RecordingAudioChunk) => {
          // Convert AudioRecordingService format to IAudioCaptureStrategy format
          const normalizedChunk: AudioChunk = {
            data: chunk.audioData, // Already base64-encoded
            format: 'webm', // AudioRecordingService uses WebM format
            sequence: this.chunkSequence++,
            timestamp: Date.now(),
            isComplete: chunk.isComplete,
          }
          this.audioChunkSubject.next(normalizedChunk)
        },
      )

    // Subscribe to audio levels
    this.audioLevelSubscription =
      this.audioRecordingService.audioLevel$.subscribe(
        (level: RecordingAudioLevelInfo) => {
          // Convert AudioRecordingService format to IAudioCaptureStrategy format
          const normalizedLevel: AudioLevelInfo = {
            currentLevel: level.currentLevel,
            isDetectingVoice: level.isDetectingVoice,
            timestamp: Date.now(),
          }
          this.audioLevelSubject.next(normalizedLevel)
        },
      )

    // Subscribe to processing status
    this.processingSubscription =
      this.audioRecordingService.processing$.subscribe(
        (isProcessing: boolean) => {
          // Update capture status based on processing state
          // When processing, we're temporarily not capturing new audio
          if (isProcessing && this.isCapturing) {
            this.statusSubject.next({
              isInitialized: true,
              isCapturing: false, // Temporarily paused during processing
              deviceId: this.deviceId,
            })
          } else if (!isProcessing && this.isCapturing) {
            this.statusSubject.next({
              isInitialized: true,
              isCapturing: true, // Resumed after processing
              deviceId: this.deviceId,
            })
          }
        },
      )
  }

  /**
   * Cleanup all subscriptions
   *
   * Called when stopping capture to prevent memory leaks.
   */
  private cleanupSubscriptions(): void {
    if (this.audioChunkSubscription) {
      this.audioChunkSubscription.unsubscribe()
      this.audioChunkSubscription = undefined
    }
    if (this.audioLevelSubscription) {
      this.audioLevelSubscription.unsubscribe()
      this.audioLevelSubscription = undefined
    }
    if (this.processingSubscription) {
      this.processingSubscription.unsubscribe()
      this.processingSubscription = undefined
    }
  }

  /**
   * Get real-time audio level stream
   *
   * Returns audio levels from AudioRecordingService's built-in frequency analysis.
   * Updates approximately every 50ms during capture.
   *
   * @returns Observable stream of audio level information
   */
  getAudioLevel$(): Observable<AudioLevelInfo> {
    return this.audioLevelSubject.asObservable()
  }

  /**
   * Get audio data chunk stream
   *
   * Returns base64-encoded WebM audio chunks when speech segments are detected.
   * Chunks are emitted when VAD detects end of speech (silence after speech).
   *
   * @returns Observable stream of audio chunks
   */
  getAudioChunk$(): Observable<AudioChunk> {
    return this.audioChunkSubject.asObservable()
  }

  /**
   * Get capture status stream
   *
   * Provides real-time status updates about the capture system.
   * Emits when initialization state or capture state changes.
   *
   * @returns Observable stream of capture status
   */
  getStatus$(): Observable<CaptureStatus> {
    return this.statusSubject.asObservable()
  }

  /**
   * Get error stream
   *
   * Emits when errors occur during capture.
   * Subscribers should check canRetry flag to determine recovery strategy.
   *
   * @returns Observable stream of capture errors
   */
  getError$(): Observable<AudioCaptureError> {
    return this.errorSubject.asObservable()
  }
}
